import { useMemo, useState } from "react";
import {
  Card,
  Button,
  Text,
  Group,
  Divider,
  Box,
  Stack,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { TOption } from "../../../types/Aula";

const useStyles = createStyles((theme) => ({
  option: {
    padding: theme.spacing.sm,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[4]}`,
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": {
      border: `1px solid black`,
    },
  },
  selected: {
    backgroundColor: theme.colors.gray[2],
  },
  correct: {
    border: `1px solid ${theme.colors.green[6]}`,
  },
  incorrect: {
    border: `1px solid ${theme.colors.red[6]}`,
    backgroundColor: theme.colors.gray[1],
  },
  correctButNotSelected: {
    border: `1px solid ${theme.colors.yellow[6]}`,
  },
  iconCorrectButNotSelected: {
    color: theme.colors.yellow[6],
  },
  icon: {
    marginLeft: theme.spacing.sm,
  },
  feedbackText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    marginBottom: theme.spacing.xs,
  },
  correctText: {
    color: theme.colors.green[6],
  },
  incorrectText: {
    color: theme.colors.red[6],
  },
  finalMessage: {
    textAlign: "center",
    fontWeight: 600,
  },
}));

interface PracticeQuizProps {
  questions: {
    id: number;
    text: string;
    options: TOption[];
  }[];
  setCompleted: React.Dispatch<React.SetStateAction<boolean>>
}

export const PracticeQuiz = ({ questions, setCompleted }: PracticeQuizProps) => {
  const { t } = useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<
    { questionId: number; selected: string[]; isReviewed: boolean }[]
  >([]);
  const [contCorrectAnswers, setContCorrectAnswers] = useState<number>(0);
  const [quizFinished, setQuizFinished] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const { classes, cx } = useStyles();

  const handleOptionClick = (option: string) => {
    setAnswers((prevAnswers) => {
      const updatedAnswers = [...prevAnswers];
      const questionAnswer = updatedAnswers.find(
        (ans) => ans.questionId === currentQuestion.id
      );
  
      if (questionAnswer && !questionAnswer.isReviewed) {
        if (allowsMultipleSelection) {
          if (questionAnswer.selected.includes(option)) {
            questionAnswer.selected = questionAnswer.selected.filter(
              (selected) => selected !== option
            );
          } else {
            questionAnswer.selected = [...questionAnswer.selected, option];
          }
        } else {
          questionAnswer.selected = [option];
        }
      } else if (!questionAnswer) {
        updatedAnswers.push({
          questionId: currentQuestion.id,
          selected: [option],
          isReviewed: false,
        });
      }
  
      return updatedAnswers;
    });
  };  
  
  const handleReview = (e: React.MouseEvent) => {
    e.preventDefault();
    setAnswers((prevAnswers) => {
      const updatedAnswers = [...prevAnswers];
      const questionAnswer = updatedAnswers.find(
        (ans) => ans.questionId === currentQuestion.id
      );
  
      if (questionAnswer && !questionAnswer.isReviewed) {
        questionAnswer.isReviewed = true;
  
        const isCorrect =
          questionAnswer.selected.length === correctOptions.length &&
          questionAnswer.selected.every((opt) => correctOptions.includes(opt));
  
        if (isCorrect) {
          setContCorrectAnswers((prev) => prev + 1);
        }
      } else if (!questionAnswer) {
        updatedAnswers.push({
          questionId: currentQuestion.id,
          selected: [],
          isReviewed: true,
        });
      }
  
      return updatedAnswers;
    });
  };  

  const handleNext = () => {
    if (currentQuestionIndex === questions.length - 1) return;
    setCurrentQuestionIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  const handleFinish = () => {
    setQuizFinished(true);
    setCompleted(true);
  };

  const handleRetake = () => {
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setContCorrectAnswers(0);
    setAnswers([]);
  };

  const getOptionClass = (option: string, isCorrect: boolean) => {
    const questionAnswer = answers.find(
      (ans) => ans.questionId === currentQuestion.id
    );
  
    const isReviewed = questionAnswer?.isReviewed;
    const isSelected = questionAnswer?.selected.includes(option);
  
    if (isReviewed) {
      if (isSelected) {
        return cx(
          classes.option,
          isCorrect ? classes.correct : classes.incorrect,
          classes.selected
        );
      }
      let correctClass = null;
      if (isCorrect) {
        correctClass = allowsMultipleSelection ? classes.correctButNotSelected : classes.correct;
      }
      return cx(classes.option, correctClass);
    }
  
    return cx(classes.option, isSelected && classes.selected);
  };
  
  const getOptionIcon = (isCorrect: boolean, option: string) => {
    const questionAnswer = answers.find(
      (ans) => ans.questionId === currentQuestion.id
    );
  
    if (!questionAnswer) return null;
  
    const { isReviewed, selected } = questionAnswer;
    const isSelected = selected.includes(option);
  
    if (!isReviewed) return null;
  
    const getIcon = (Icon: React.ElementType, color?: string, className?: string) => (
      <Icon size={16} color={color} className={className} />
    );
  
    if (isSelected) return isCorrect ? getIcon(FaCheck, "green") : getIcon(FaTimes, "red");
  
    if (isCorrect) return allowsMultipleSelection ? getIcon(FaCheck, undefined, classes.iconCorrectButNotSelected) : getIcon(FaCheck, "green");
  
    return null;
  };  
  

  const getFeedbackText = () => {
    const questionAnswer = answers.find(
      (ans) => ans.questionId === currentQuestion.id
    );
  
    if (!questionAnswer?.isReviewed || !questionAnswer.selected.length) return null;
  
    const isFullyCorrect =
      questionAnswer.selected.length === correctOptions.length &&
      questionAnswer.selected.every((opt) => correctOptions.includes(opt));
  
    return isFullyCorrect ? (
      <Text className={cx(classes.feedbackText, classes.correctText)}>
        {t("quiz.correct")}
      </Text>
    ) : (
      <Text className={cx(classes.feedbackText, classes.incorrectText)}>
        {t("quiz.incorrect")}
      </Text>
    );
  };

  const correctOptions = useMemo(() => {
    return currentQuestion.options
      .filter((opt) => opt.is_correct)
      .map((opt) => opt.text);
  }, [currentQuestion.options]);
  const allowsMultipleSelection = correctOptions.length > 1;

  return (
    <Card
      shadow="md"
      padding="xl"
      radius="md"
      maw={600}
      m="auto"
      withBorder
    >
      {quizFinished ? (
        <Stack gap="md" align="center">
          <FaCheck color="green" size={40} />
          <Text className={classes.finalMessage}>
            {t("quiz.score")} {contCorrectAnswers} / {questions.length} ({(contCorrectAnswers / questions.length * 100).toFixed(0)}%)
          </Text>
          <Button onClick={handleRetake}>{t("actions.retake")}</Button>
        </Stack>
      ) : (
        <>
          <Text size="lg" fw={500} ta="center" c="dimmed">
            {currentQuestionIndex + 1}/{questions.length}
          </Text>
          <Divider my="sm" />
          <Text size="xl" fw={600} ta="center" mt="lg" mb="lg">
            {currentQuestion.text}
          </Text>
          {getFeedbackText()}
          <Stack gap="md">
            <Text ta="center" size="sm" c="dimmed" fw={500}>
              {allowsMultipleSelection
                ? t("quiz.selectMultiple")
                : ""}
            </Text>
            {currentQuestion.options.map((option, index) => (
              <Box
                key={index}
                onClick={() => handleOptionClick(option.text)}
                className={getOptionClass(option.text, option.is_correct)}
              >
                <Text ta="center">{option.text}</Text>
                {getOptionIcon(option.is_correct, option.text)}
              </Box>
            ))}
          </Stack>
          <Group
            justify={currentQuestionIndex > 0 ? "space-between" : "flex-end"}
            mt="xl"
          >
            {currentQuestionIndex > 0 && (
              <Button variant="outline" onClick={handleBack}>
                {t("actions.back")}
              </Button>
            )}
            {answers.find((ans) => ans.questionId === currentQuestion.id)?.isReviewed ? (
              currentQuestionIndex === questions.length - 1 ? (
                <Button onClick={handleFinish}>{t("actions.submit")}</Button>
              ) : (
                <Button onClick={handleNext}>{t("actions.next")}</Button>
              )
            ) : (
              <Button
                onClick={handleReview}
                disabled={!answers.find(
                  (ans) => ans.questionId === currentQuestion.id
                )?.selected}
              >
                {t("actions.review")}
              </Button>
            )}
          </Group>
        </>
      )}
    </Card>
  );
};
